import React, { useEffect, useState, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SubTitle from "../common/SubTitle";
import Cards from "../common/Cards";
import SearchFilter from "../common/SearchFilter";
import Pagination from "../common/Pagination";
import config from "../../config.js";
import Update_Icon from "../../images/table/Update_icon.svg";
import Track_Icon from "../../images/table/View_icon.svg";
import Delete_Icon from "../../images/table/Delete_icon.svg";
import Schedule_Icon from "../../images/table/Schedule_icon.svg";
import DeleteModal from "../common/DeleteModal";
import DynamicTable from "../common/DynamicTable";
import { usePermission } from "../../hooks/usePermission";
import { useTranslation } from "react-i18next";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";

const headers = [
  { key: "Route ID", value: "Route ID" },
  { key: "Route Name", value: "Routes" },
  { key: "Stops", value: "Stops" },
  { key: "Vehicles", value: "Vehicles" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

const Route = () => {
  const [routeData, setRouteData] = useState([]);
  const [filteredRouteData, setFilteredRouteData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [connectedStop,setConnectedStop]=useState(0);
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);

  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;

  useEffect(() => {
      fetchRoutes();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchInput, filterValues, routeData]);

  // Generate actions based on permissions
  const getCustomActions = () => {
    const actions = [];

    if (hasPermission("/api/route", "PUT")) {
      actions.push({
        id: "update",
        label: "Update",
        icon: Update_Icon,
        path: "/route/add",
      });
    }

    if (hasPermission("/api/route/id", "GET")) {
      actions.push({
        id: "monitor",
        label: "Monitor",
        icon: Track_Icon,
        path: "/route/track",
      });
    }

    if (hasPermission("/api/route/schedules", "GET")) {
      actions.push({
        id: "schedule",
        label: "Schedule",
        icon: Schedule_Icon,
        path: "/route/schedules",
      });
    }

    if (hasPermission("/api/route", "DELETE")) {
      actions.push({
        id: "delete",
        label: "Delete",
        className: "delete",
        icon: Delete_Icon,
        path: "",
      });
    }

    return actions;
  };

  const fetchRoutes = async () => {
    setIsLoading(true);
    try {
      const routesResponse = await axios.get(
        `${config.baseURL}/route/organization`,
        {
          params: {
            organizationId: orgId,
          },
          withCredentials: true,
        }
      );

      if (routesResponse.status === 401) {
        console.log("Unauthorized request");
        navigate("/");
        return;
      }

      // Process each route individually to fetch stops
      const routesWithStops = await Promise.all(
        routesResponse.data.map(async (route) => {
          const stops = await fetchStopsForRoute(route);
          return { ...route, stops };
        })
      );

      // console.log("Routes with stops:", routesWithStops);
      
      let totalStop=0;
      routesWithStops.forEach(route=>{
        totalStop=totalStop+ (route.stops ? route.stops.length : 0);
      })
      
      setConnectedStop(totalStop);
      setRouteData(routesWithStops);
      setFilteredRouteData(routesWithStops);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log("Unauthorized request");
        navigate("/");
        return;
      }
      console.log("Error in fetching routes by organization", error);
    } finally {
      setIsLoading(false);
    }
  };



  // Fetch stops for each route individually
  const fetchStopsForRoute = async (route) => {
    const stops = [];
    for (const coordinate of route.routeCoordinates) {
      try {
        const stopsResponse = await axios.get(
          `${config.baseURL}/stop/organization/location`,
          {
            params: {
              organizationId: orgId,
              longitude: coordinate.longitude,
              latitude: coordinate.latitude,
            },
            withCredentials: true,
          }
        );
        stops.push(stopsResponse.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          console.log("Unauthorized request");
          navigate("/");
          return;
        }
        console.log("Error in fetching stops by coordinates", error);
      }
    }
    return stops;
  };



  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  const handleFilterChange = (filterValues) => {
    setFilterValues(filterValues);
    console.log("Filter values:", filterValues);
  };

  const applyFilters = () => {
    let filtered = [...routeData];

    // Apply search filter
    if (searchInput) {
      const searchTerm = searchInput.toLowerCase();
      filtered = filtered.filter(
        (route) =>
          (route.routeId && route.routeId.toLowerCase().includes(searchTerm)) ||
          (route.name && route.name.toLowerCase().includes(searchTerm)) ||
          (route.vehicles &&
            route.vehicles.some((vehicle) =>
              vehicle.name.toLowerCase().includes(searchTerm)
            )) ||
          (route.stops &&
            route.stops.some((stop) =>
              stop.name.toLowerCase().includes(searchTerm)
            ))
      );
    }

    // Apply status filter
    if (filterValues.status) {
      filtered = filtered.filter(
        (route) => route.status === filterValues.status
      );
    }

    setFilteredRouteData(filtered);
  };

  // Helper function to extract stop names or points
  const getStopPoints = (stops) => {
    // Flatten stops array and extract names
    const flattenedStops = stops?.flat() || [];

    if (flattenedStops.length === 0) {
      return "No stops";
    }

    // If only one stop, return it
    if (flattenedStops.length === 1) {
      return flattenedStops[0].name || "Unnamed stop";
    }

    // Get first and last stop
    const firstStop = flattenedStops[0].name || "Start";
    const lastStop = flattenedStops[flattenedStops.length - 1].name || "End";

    return `${firstStop} → ${lastStop}`;
  };

  // Helper function to get vehicle names as comma-separated string
  const getVehicleNames = (vehicles) => {
    if (!vehicles || vehicles.length === 0) {
      return "No vehicles";
    }

    // Join all vehicle names with commas
    return vehicles
      .map((vehicle) => vehicle.name || "Unnamed vehicle")
      .join(", ");
  };

  // Format data according to what DynamicTable expects
  const formatDataForTable = () => {
    return filteredRouteData.map((route) => {
      console.log("Formatting route:", route);
      // Create an object with properties matching header names
      const formattedData = {
        "Route ID": route.routeId || "N/A",
        "Route Name": route.name || "N/A",
        Stops: getStopPoints(route.stops),
        Vehicles: getVehicleNames(route.vehicles),
      };

      // Add original ID and data for action handling
      formattedData.id = route.id;
      formattedData.originalData = route;

      return formattedData;
    });
  };

  // Delete modal handlers
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setRouteToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (routeToDelete) {
      console.log("Deleting route ID:", routeToDelete.id);
      try {
        const userState = localStorage.getItem("userState");
        const parsedUserState = JSON.parse(userState);
        const orgId = parsedUserState.user.organizationId;
        const response = await axios.delete(`${config.baseURL}/route`, {
          data: { id: Number(routeToDelete.id), organizationId: orgId },
          withCredentials: true,
        });
        console.log("Route deleted successfully:", response.data);
        fetchRoutes();
      } catch (error) {
        console.log("Error in deleting route: ", error);
      }
    }
    setIsDeleteModalOpen(false);
    setRouteToDelete(null);
  };

  const handleActionClick = (actionId, rowIndex, actionObj) => {
    // Get the actual route data for this row
    const route =
      filteredRouteData[rowIndex].originalData || filteredRouteData[rowIndex];
    console.log("Route data being passed:", route);

    switch (actionId) {
      case "update":
        console.log("Update route data:", route);
        navigate(actionObj.path, { state: { rowData: route } });
        break;
      case "monitor":
        const routeDataForTracking = {
          id: route.id,
          name: route.name,
          routeId: route.routeId,
          stops: route.stops || [],
          vehicles: route.vehicles || [],
          routeCoordinates: route.routeCoordinates || [],
          schedules: route.schedules || [],
          routeColor: route.routeColor || "#000000",
        };
        console.log("Monitor route data:", routeDataForTracking);
        navigate(actionObj.path, {
          state: {
            selectedRouteData: routeDataForTracking,
          },
        });
        break;
      case "schedule":
        navigate(actionObj.path, { state: { id: route.id } });
        break;
      case "delete":
        setRouteToDelete(route);
        setIsDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleAddRoute = () => {
    navigate("/route/add");
  };

  const handleViewAllRoutesOnMap = () => {
    navigate("/route/allroutes");
  };

  const canCreateRoute = () => {
    return hasPermission("/api/route", "POST");
  };
  const { t } = useTranslation();
  console.log(routeData);
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  return (
    <div style={{ minHeight: "100vh" }}>
      <SubTitle
        title="routes"
        path={`${orgName || "organization"} > Routes`}
        add={canCreateRoute() ? "createRoute" : null}
        addPath={canCreateRoute() ? "/route/add" : null}
        onAddClick={canCreateRoute() ? handleAddRoute : null}
        track={"Monitor Routes"}
        onTrackClick={handleViewAllRoutesOnMap}
      />

      <Cards
        cardData={[
          {
            title: "totalRoutes",
            value: routeData?.length || 0,
            backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
          {
            title: "connectedStops",
            value: false
              ? connectedStop
              : routeData?.reduce((total, route) => {
                  if (Array.isArray(route.stops)) {
                    return total + route.stops.length;
                  }
                  return total;
                }, 0) || 0,
            backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
            titleColor: TEXT_COLORS.WHITE,
            valueColor: TEXT_COLORS.WHITE,
          },
        ]}
      />

      <div className="table_container">
        <SearchFilter
          searchPlaceholder="search"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />

        <Suspense fallback={<div className="loader"></div>}>
          <DynamicTable
            headers={headers}
            data={formatDataForTable()}
            onActionClick={handleActionClick}
            actions={getCustomActions()}
            loading={isLoading}
          />
        </Suspense>

        {/* <Pagination
          totalItems={filteredRouteData?.length || 0}
        /> */}
      </div>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Route!"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {routeToDelete?.name || t("this route")}
            </span>
            ? {t("This action cannot be undone.")}
          </>
        }
      />
    </div>
  );
};

export default Route;
